import { ChevronsUpDown, Plus } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuShortcut,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  useSidebar,
} from "@/components/ui/sidebar";
import { useWorkspace } from "@/hooks/useWorkspace";
import { useNavigate } from "react-router-dom";
import { isClientPortalHost } from "@/lib/client-portal-host";

function getWorkspaceInitials(name: string) {
  return name
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase() || "")
    .join("");
}

function WorkspaceAvatar({
  name,
  logoUrl,
  size,
}: {
  name: string;
  logoUrl?: string;
  size: "sm" | "md";
}) {
  const sizeClass = size === "md" ? "size-8 rounded-lg" : "size-6 rounded-sm";

  if (logoUrl) {
    return (
      <div className={`shrink-0 overflow-hidden bg-muted ${sizeClass}`}>
        <img src={logoUrl} alt={name} className="h-full w-full object-cover" />
      </div>
    );
  }

  return (
    <div className={`shrink-0 flex items-center justify-center bg-[#4A42A8] text-white ${sizeClass}`}>
      <span className={size === "md" ? "text-xs font-semibold" : "text-[10px] font-semibold"}>
        {getWorkspaceInitials(name)}
      </span>
    </div>
  );
}

export function WorkspaceSwitcher() {
  const { isMobile } = useSidebar();
  const { workspaces, currentWorkspace, setCurrentWorkspace } = useWorkspace();
  const navigate = useNavigate();
  const canCreateWorkspace = workspaces.some((workspace) => workspace.role !== "client");
  const isClientPortal = isClientPortalHost();
  const handleCreateWorkspace = () => {
    if (!isClientPortal) {
      navigate("/onboarding");
    }
  };

  if (!currentWorkspace) return null;
  if (!canCreateWorkspace && workspaces.length <= 1) return null;

  return (
    <SidebarMenu>
      <SidebarMenuItem>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <SidebarMenuButton
              size="lg"
              className="data-[state=open]:bg-sidebar-accent data-[state=open]:text-sidebar-accent-foreground text-sidebar-foreground"
            >
              <WorkspaceAvatar
                name={currentWorkspace.name}
                logoUrl={currentWorkspace.logo_url}
                size="md"
              />
              <div className="grid flex-1 text-left text-sm leading-tight">
                <span className="truncate font-semibold">
                  {currentWorkspace.name}
                </span>
                <span className="truncate text-xs">{currentWorkspace.role}</span>
              </div>
              <ChevronsUpDown className="ml-auto" />
            </SidebarMenuButton>
          </DropdownMenuTrigger>
          <DropdownMenuContent
            className="w-[--radix-dropdown-menu-trigger-width] min-w-56 rounded-lg"
            align="start"
            side={isMobile ? "bottom" : "right"}
            sideOffset={4}
          >
            <DropdownMenuLabel className="text-xs text-muted-foreground">
              Agências
            </DropdownMenuLabel>
            {workspaces.map((workspace) => (
              <DropdownMenuItem
                key={workspace.id}
                onClick={() => setCurrentWorkspace(workspace)}
                className="gap-2 p-2"
              >
                <WorkspaceAvatar
                  name={workspace.name}
                  logoUrl={workspace.logo_url}
                  size="sm"
                />
                {workspace.name}
                {workspace.id === currentWorkspace.id && (
                  <DropdownMenuShortcut>Atual</DropdownMenuShortcut>
                )}
              </DropdownMenuItem>
            ))}
            {canCreateWorkspace && !isClientPortal && (
              <>
                <DropdownMenuSeparator />
                <DropdownMenuItem className="gap-2 p-2" onClick={handleCreateWorkspace}>
                  <div className="flex size-6 items-center justify-center rounded-md border bg-background">
                    <Plus className="size-4" />
                  </div>
                  <div className="font-medium text-muted-foreground">Adicionar Agência</div>
                </DropdownMenuItem>
              </>
            )}
          </DropdownMenuContent>
        </DropdownMenu>
      </SidebarMenuItem>
    </SidebarMenu>
  );
}
